import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, X, FileText, ShieldCheck, AlertTriangle,
  CheckCircle2, CircleAlert, CircleX, ThumbsUp, ThumbsDown, Share2, Loader2,
  MinusCircle, Eraser, Info,
} from "lucide-react";
import { Header } from "../components/Header";
import { AdminNav } from "../components/AdminNav";
import {
  fetchQaSearches, fetchQaSearch, setAuditRating, addAuditNote, deleteAuditNote,
  fetchFeedbackAudit, bulkSetFeedback, setAdminFeedback,
} from "../lib/api";
import { QA } from "../constants/testIds";

const RATING_OPTIONS = [
  { value: "", label: "All ratings" },
  { value: "unrated", label: "Unrated" },
  { value: "excellent", label: "Excellent" },
  { value: "acceptable", label: "Acceptable" },
  { value: "poor", label: "Poor" },
];
const FLAG_OPTIONS = [
  { value: "", label: "All flags" },
  { value: "failed", label: "Failed" },
  { value: "empty", label: "Empty" },
  { value: "low_confidence", label: "Low confidence" },
  { value: "missing_category", label: "Missing category" },
];

const RATING_STYLE = {
  excellent:  { dot: "bg-emerald-500", text: "text-emerald-400", border: "border-emerald-500/40", label: "Excellent" },
  acceptable: { dot: "bg-amber-400",   text: "text-amber-300",   border: "border-amber-400/40",   label: "Acceptable" },
  poor:       { dot: "bg-red-500",     text: "text-red-400",     border: "border-red-500/40",     label: "Poor" },
};

const FLAG_STYLE = {
  failed:            { color: "text-red-400 bg-red-500/10 border-red-500/30",      label: "failed" },
  empty:             { color: "text-orange-400 bg-orange-500/10 border-orange-500/30", label: "empty" },
  low_confidence:    { color: "text-amber-300 bg-amber-400/10 border-amber-400/30", label: "low conf" },
  missing_category:  { color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",    label: "no category" },
};

const FlagPill = ({ flag }) => {
  const s = FLAG_STYLE[flag] || { color: "text-muted-foreground bg-secondary border-border", label: flag };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.color}`}>{s.label}</span>
  );
};

const RatingPill = ({ rating }) => {
  if (!rating) return <span className="text-xs text-muted-foreground">—</span>;
  const s = RATING_STYLE[rating];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${s.border} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const Drawer = ({ open, onClose, search, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !search) { setDetail(null); return; }
    fetchQaSearch(search.id).then(setDetail).catch(() => setDetail(null));
  }, [open, search]);

  if (!open || !search) return null;
  const d = detail || search;
  const response = d.response || {};
  const verdict = response.verdict || {};

  const setRating = async (r) => {
    setBusy(true);
    try {
      await setAuditRating(d.id, r === d.audit_rating ? null : r);
      const fresh = await fetchQaSearch(d.id);
      setDetail(fresh);
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      await addAuditNote(d.id, note.trim());
      const fresh = await fetchQaSearch(d.id);
      setDetail(fresh);
      setNote("");
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const removeNote = async (noteId) => {
    setBusy(true);
    try {
      await deleteAuditNote(d.id, noteId);
      const fresh = await fetchQaSearch(d.id);
      setDetail(fresh);
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <button className="flex-1 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <aside className="w-full max-w-xl bg-card border-l border-border h-full overflow-y-auto scroll-thin">
        <div className="sticky top-0 z-10 bg-card border-b border-border px-5 py-3 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">QA Detail</span>
          <button
            onClick={onClose}
            data-testid={QA.drawerClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Query</div>
            <p className="text-sm leading-relaxed">{d.query}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Best product</div>
              <div className="text-foreground/90">{d.best_product || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Category</div>
              <div className="text-foreground/90">{d.category || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Verdict</div>
              <div className="text-foreground/90 capitalize">{(d.verdict_type || "—").replace(/_/g, " ")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Confidence</div>
              <div className="text-foreground/90">{d.confidence != null ? `${d.confidence}%` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Value</div>
              <div className="text-foreground/90">{d.value_score != null ? `${d.value_score}/10` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">User feedback</div>
              <div className="text-foreground/90 text-xs">
                <ThumbsUp className="inline w-3 h-3 text-emerald-500 mr-1" />{d.helpful_votes ?? 0}
                <span className="mx-2 text-muted-foreground">·</span>
                <ThumbsDown className="inline w-3 h-3 text-red-400 mr-1" />{d.unhelpful_votes ?? 0}
                <span className="mx-2 text-muted-foreground">·</span>
                <Share2 className="inline w-3 h-3 text-cyan-400 mr-1" />{d.share_count ?? 0}
              </div>
            </div>
          </div>

          {(d.flags || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Flags</div>
              <div className="flex flex-wrap gap-1.5">
                {d.flags.map((f) => <FlagPill key={f} flag={f} />)}
              </div>
            </div>
          )}

          {d.error && (
            <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-3 text-xs text-red-300">
              <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />AI error</div>
              <code className="break-all">{d.error}</code>
            </div>
          )}

          {response.why && (
            <div className="bg-secondary/40 border border-border rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Why</div>
              <p className="text-sm text-foreground/90 leading-relaxed">{response.why}</p>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Audit rating</div>
            <div className="flex gap-2">
              {["excellent", "acceptable", "poor"].map((r) => {
                const active = d.audit_rating === r;
                const s = RATING_STYLE[r];
                return (
                  <button
                    key={r}
                    onClick={() => setRating(r)}
                    disabled={busy}
                    data-testid={QA.ratingBtn(r)}
                    className={`flex-1 flex items-center justify-center gap-1.5 text-xs px-3 py-2.5 rounded-lg border transition ${
                      active ? `${s.border} ${s.text} bg-secondary` : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Improvement notes</div>
            <form onSubmit={submitNote} className="flex gap-2 mb-3">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid={QA.noteInput}
                placeholder="e.g. Recommendation too expensive."
                className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/60"
              />
              <button
                type="submit"
                disabled={busy || !note.trim()}
                data-testid={QA.noteSubmit}
                className="text-xs px-3 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-40 transition"
              >
                Add
              </button>
            </form>
            <div className="space-y-2">
              {(d.audit_notes || []).length === 0 && <div className="text-xs text-muted-foreground">No notes yet.</div>}
              {(d.audit_notes || []).map((n, i) => (
                <div key={n.id} data-testid={QA.noteItem(i)} className="bg-secondary/40 border border-border rounded-lg p-3 text-sm">
                  <p className="text-foreground/90 leading-relaxed">{n.note}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString("en-GB")}</span>
                    <button onClick={() => removeNote(n.id)} className="text-[10px] text-red-400 hover:underline">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

const SummaryStrip = () => null; // stats card row lives on Overview; QA page is focused

export default function AdminQA() {
  const navigate = useNavigate();
  const [rating, setRating] = useState("");
  const [flag, setFlag] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("sbi_admin_token")) navigate("/admin");
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    fetchQaSearches({ rating: rating || undefined, flag: flag || undefined, q: debouncedQ || undefined, limit: 100 })
      .then((d) => { setData(d); setSelected(new Set()); })
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("sbi_admin_token");
          navigate("/admin");
        }
      })
      .finally(() => setLoading(false));
    fetchFeedbackAudit().then(setAudit).catch(() => {});
  }, [rating, flag, debouncedQ, navigate]);

  useEffect(load, [load]);

  const logout = () => {
    localStorage.removeItem("sbi_admin_token");
    navigate("/admin");
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === data.items.length && data.items.length > 0) setSelected(new Set());
    else setSelected(new Set(data.items.map((s) => s.id)));
  };

  const applyBulk = async (feedback) => {
    if (selected.size === 0) return;
    await bulkSetFeedback(Array.from(selected), feedback);
    load();
  };

  return (
    <div className="min-h-screen bg-background" data-testid={QA.page}>
      <Header />
      <main className="max-w-7xl mx-auto px-5 py-8">
        <AdminNav onLogout={logout} />

        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">QA Searches</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Audit every recommendation. Rate, flag and annotate before scaling the engine.
            </p>
          </div>
          <div className="text-xs text-muted-foreground">{data.total} matching</div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-3 mb-5 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid={QA.search}
              placeholder="Search query, product or category…"
              className="w-full bg-background border border-border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-emerald-500/60"
            />
          </div>
          <select
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            data-testid={QA.ratingFilter}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/60"
          >
            {RATING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={flag}
            onChange={(e) => setFlag(e.target.value)}
            data-testid={QA.flagFilter}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/60"
          >
            {FLAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Audit banner — make the approval formula transparent */}
        {audit && (
          <div
            data-testid={QA.auditBanner}
            className="bg-card border border-border rounded-2xl p-4 mb-3 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Info className="w-3.5 h-3.5 text-emerald-500" />
              <span>How approval is calculated</span>
            </div>
            <div className="text-xs text-foreground/80 leading-relaxed flex-1">
              <span className="font-mono text-emerald-400">
                ({audit.user_helpful_votes} 👍 + {audit.admin_helpful} admin-helpful) /
                ({audit.helpful_total} + {audit.unhelpful_total}) = {audit.approval_percent ?? "—"}%
              </span>
              <span className="text-muted-foreground ml-2">
                · {audit.no_feedback_count} of {audit.total_recommendations} recs have NO feedback (excluded);
                {" "}{audit.admin_neutral} marked neutral (excluded)
              </span>
            </div>
          </div>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div
            data-testid={QA.bulkBar}
            className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-3 mb-3 flex flex-wrap items-center gap-2"
          >
            <span className="text-xs text-emerald-300 font-semibold mr-2">
              {selected.size} selected — mark as
            </span>
            <button
              onClick={() => applyBulk("helpful")}
              data-testid={QA.bulkBtn("helpful")}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition"
            >
              <ThumbsUp className="w-3.5 h-3.5" /> Helpful
            </button>
            <button
              onClick={() => applyBulk("neutral")}
              data-testid={QA.bulkBtn("neutral")}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition"
            >
              <MinusCircle className="w-3.5 h-3.5" /> Neutral
            </button>
            <button
              onClick={() => applyBulk("not_helpful")}
              data-testid={QA.bulkBtn("not_helpful")}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition"
            >
              <ThumbsDown className="w-3.5 h-3.5" /> Not Helpful
            </button>
            <button
              onClick={() => applyBulk(null)}
              data-testid={QA.bulkBtn("clear")}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-border text-muted-foreground hover:bg-secondary transition"
            >
              <Eraser className="w-3.5 h-3.5" /> Clear
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground"
            >
              Deselect all
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {loading && data.items.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" /> Loading…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="px-3 py-3">
                      <input
                        type="checkbox"
                        data-testid={QA.selectAll}
                        checked={data.items.length > 0 && selected.size === data.items.length}
                        onChange={toggleAll}
                        className="accent-emerald-500"
                      />
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">Query</th>
                    <th className="text-left px-4 py-3 font-semibold">Best product</th>
                    <th className="text-left px-4 py-3 font-semibold">Verdict</th>
                    <th className="text-left px-4 py-3 font-semibold">Conf.</th>
                    <th className="text-left px-4 py-3 font-semibold">👍/👎</th>
                    <th className="text-left px-4 py-3 font-semibold">Admin</th>
                    <th className="text-left px-4 py-3 font-semibold">Shares</th>
                    <th className="text-left px-4 py-3 font-semibold">Flags</th>
                    <th className="text-left px-4 py-3 font-semibold">Rating</th>
                    <th className="text-left px-4 py-3 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <tr><td colSpan={11} className="text-center text-sm text-muted-foreground py-8">No searches match.</td></tr>
                  )}
                  {data.items.map((s) => (
                    <tr
                      key={s.id}
                      data-testid={QA.row(s.id)}
                      onClick={(e) => {
                        if (e.target.tagName === "INPUT") return;
                        setActive(s);
                      }}
                      className={`border-b border-border/60 hover:bg-secondary/30 cursor-pointer ${selected.has(s.id) ? "bg-emerald-500/5" : ""}`}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={QA.rowCheckbox(s.id)}
                          checked={selected.has(s.id)}
                          onChange={() => toggleRow(s.id)}
                          className="accent-emerald-500"
                        />
                      </td>
                      <td className="px-4 py-3 max-w-[260px] truncate" title={s.query}>{s.query}</td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[180px] truncate">{s.best_product || "—"}</td>
                      <td className="px-4 py-3 text-xs capitalize">{(s.verdict_type || "—").replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{s.confidence != null ? `${s.confidence}%` : "—"}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className="text-emerald-500 font-semibold">{s.helpful_votes ?? 0}</span>
                        <span className="text-muted-foreground"> / </span>
                        <span className="text-red-400 font-semibold">{s.unhelpful_votes ?? 0}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-cyan-400 font-semibold">{s.share_count ?? 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(s.flags || []).map((f) => <FlagPill key={f} flag={f} />)}
                        </div>
                      </td>
                      <td className="px-4 py-3"><RatingPill rating={s.audit_rating} /></td>
                      <td className="px-4 py-3 text-[11px] text-muted-foreground whitespace-nowrap">
                        {new Date(s.created_at).toLocaleString("en-GB")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Drawer open={!!active} onClose={() => setActive(null)} search={active} onChanged={load} />
    </div>
  );
}
